import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

/** Props à poser sur la poignée — le hook ne rend rien, il fournit le geste. */
export interface EdgeResizeHandleProps {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
}

/**
 * Redimensionnement d'un panneau ancré à un bord de l'écran, en tirant son bord
 * intérieur.
 *
 * WHY ce hook existe : le mécanisme vivait en 18 lignes dans `AppSidebar`, et le
 * panneau d'aperçu (bord droit) en avait besoin à l'identique. Le dupliquer aurait
 * fait diverger deux drags qui doivent se comporter pareil — c'est le même geste
 * pour l'avocat.
 *
 * ⚠️ POINTER EVENTS + CAPTURE, jamais `mousemove`/`mouseup` sur `document`
 * (2026-09-15). Une iframe avale les événements souris de la page parente : dès
 * que le curseur passait au-dessus du PDF affiché dans l'aperçu, la page ne
 * recevait plus ni `mousemove` ni `mouseup`. Le drag ne se terminait donc jamais
 * au relâchement et le panneau continuait de suivre la souris jusqu'au clic
 * suivant — le défaut signalé sur les pièces. `setPointerCapture` fait livrer tous
 * les événements suivants à la poignée elle-même, quoi qu'il y ait sous le
 * curseur. C'est déjà le choix fait pour les coins de la fenêtre flottante de
 * l'assistant (`DossierCaseDiscoveryShell`).
 *
 * Le hook ne borne rien : il rend la largeur brute déduite du curseur, et c'est
 * l'appelant (son store) qui la clampe.
 */
export function useEdgeResize({
  side,
  onWidthChange,
  onResizingChange,
}: {
  /** Bord auquel le panneau est ancré — détermine le sens de lecture du curseur. */
  side: 'left' | 'right';
  /** Largeur brute (px) déduite de la position du curseur, à chaque mouvement. */
  onWidthChange: (width: number) => void;
  /** Début/fin du drag — l'appelant s'en sert pour couper ses transitions. */
  onResizingChange?: (resizing: boolean) => void;
}): { isResizing: boolean; resizeHandleProps: EdgeResizeHandleProps } {
  const [isResizing, setIsResizing] = useState(false);
  const activePointerRef = useRef<number | null>(null);

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    event.preventDefault();
    activePointerRef.current = event.pointerId;
    // `setPointerCapture` peut manquer (jsdom, très vieux navigateur) : le drag
    // reste fonctionnel tant que le curseur ne survole pas d'iframe.
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setIsResizing(true);
  }, []);

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (activePointerRef.current !== event.pointerId) return;
    // Panneau ancré à gauche : la largeur EST l'abscisse du curseur. Ancré à
    // droite : c'est la distance qui reste jusqu'au bord droit de la fenêtre.
    onWidthChange(side === 'left' ? event.clientX : window.innerWidth - event.clientX);
  }, [onWidthChange, side]);

  const endResize = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (activePointerRef.current !== event.pointerId) return;
    activePointerRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (!isResizing) return;
    // Curseur global + pas de sélection de texte parasite pendant le drag.
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  useEffect(() => {
    onResizingChange?.(isResizing);
  }, [isResizing, onResizingChange]);

  return {
    isResizing,
    resizeHandleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endResize,
      onPointerCancel: endResize,
    },
  };
}
